import { PaywallDialog } from '@/components/shared/PaywallDialog';
import { TeamAddDialog } from '@/components/team/TeamAddDialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';
import { useTeamContext } from '@/contexts/TeamContext';
import { type Team } from '@/hooks/useTeams';
import { useTeamStatus } from '@/hooks/useTeamStatus';
import { ChevronsUpDown } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export const TeamSwitcher = () => {
  const { isMobile } = useSidebar();
  const { activeTeam, setActiveTeam, teams } = useTeamContext();
  const { teamOnboardingComplete } = useTeamStatus();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isPaywallOpen, setIsPaywallOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [newTeam, setNewTeam] = useState<Team | undefined>(undefined);
  const navigate = useNavigate();

  const handleTeamSelect = useCallback(
    (team: Team) => {
      setActiveTeam(team);
      setIsDropdownOpen(false);
      navigate('/');
    },
    [setActiveTeam, navigate],
  );

  const handleTeamAdd = () => {
    setIsDropdownOpen(false);
    setIsDialogOpen(true);
  };

  const handleTeamAddSuccess = (team: Team) => {
    setNewTeam(team);
    setIsDialogOpen(false);
    setIsPaywallOpen(true);
  };

  if (!teams?.length) return null;

  return (
    <>
      <SidebarMenu>
        <SidebarMenuItem>
          <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton
                size="lg"
                className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground">
                <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
                  {activeTeam?.name?.[0] || '?'}
                </div>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">
                    {activeTeam?.name || 'Select Team'}
                  </span>
                  <span className="truncate text-xs">
                    {activeTeam?.description && teamOnboardingComplete
                      ? activeTeam?.description
                      : 'No description set'}
                  </span>
                </div>
                <ChevronsUpDown className="ml-auto" />
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
              align="start"
              side={isMobile ? 'bottom' : 'right'}
              sideOffset={4}>
              <DropdownMenuLabel className="text-xs text-muted-foreground">
                Teams
              </DropdownMenuLabel>
              {teams?.map((team) => {
                const isCurrentTeam = team.id === activeTeam?.id;

                return (
                  <DropdownMenuItem
                    key={team.id}
                    onClick={() => !isCurrentTeam && handleTeamSelect(team)}
                    disabled={isCurrentTeam}
                    className={`group transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground disabled:opacity-100  cursor-pointer ${
                      isCurrentTeam
                        ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                        : ''
                    }`}>
                    <div className="flex w-full items-center">
                      <p className="text-sm">{team.name}</p>
                    </div>
                  </DropdownMenuItem>
                );
              })}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={handleTeamAdd}
                className="transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground cursor-pointer">
                Add team
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>

      <TeamAddDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        onSuccess={handleTeamAddSuccess}
      />

      <PaywallDialog
        open={isPaywallOpen}
        onOpenChange={setIsPaywallOpen}
        newTeam={newTeam}
      />
    </>
  );
};
